import React, { useState, useEffect, useMemo } from "react";
import styles from "./SupplierFormModal.module.css";

const EMPTY_FORM = {
  supplierName: "",
  itemName: "",
  quantity: "",
  unitPrice: "",
  paidAmount: "",
  transactionDate: "",
  notes: "",
};

const SupplierFormModal = ({ isOpen, onClose, onSubmit, initialData }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (initialData) {
      setForm({
        supplierName: initialData.supplierName || "",
        itemName: initialData.itemName || "",
        quantity: initialData.quantity ?? "",
        unitPrice: initialData.unitPrice ?? "",
        paidAmount: initialData.paidAmount ?? "",
        transactionDate: initialData.transactionDate ? initialData.transactionDate.slice(0, 10) : "",
        notes: initialData.notes || "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setErrors({});
  }, [initialData, isOpen]);

  const totalAmount = useMemo(() => {
    const qty = Number(form.quantity) || 0;
    const price = Number(form.unitPrice) || 0;
    return qty * price;
  }, [form.quantity, form.unitPrice]);

  const remainingAmount = useMemo(() => {
    return totalAmount - (Number(form.paidAmount) || 0);
  }, [totalAmount, form.paidAmount]);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.supplierName.trim()) newErrors.supplierName = "اسم المورد مطلوب";
    if (!form.itemName.trim()) newErrors.itemName = "اسم الصنف مطلوب";
    if (!form.quantity || Number(form.quantity) <= 0) newErrors.quantity = "الكمية يجب أن تكون أكبر من صفر";
    if (!form.unitPrice || Number(form.unitPrice) <= 0) newErrors.unitPrice = "سعر الوحدة يجب أن يكون أكبر من صفر";
    if (form.paidAmount !== "" && Number(form.paidAmount) < 0) newErrors.paidAmount = "المبلغ المدفوع لا يمكن أن يكون سالبًا";
    if (!form.transactionDate) newErrors.transactionDate = "التاريخ مطلوب";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      await onSubmit(
        {
          supplierName: form.supplierName.trim(),
          itemName: form.itemName.trim(),
          quantity: Number(form.quantity),
          unitPrice: Number(form.unitPrice),
          paidAmount: Number(form.paidAmount) || 0,
          transactionDate: new Date(form.transactionDate).toISOString(),
          notes: form.notes.trim(),
        },
        setErrors
      );
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()} dir="rtl">
        <div className={styles.header}>
          <h3>{initialData ? "تعديل معاملة مورد" : "إضافة معاملة مورد"}</h3>
          <button className={styles.closeBtn} onClick={onClose} type="button" aria-label="إغلاق">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.field}>
            <label>اسم المورد</label>
            <input
              type="text"
              value={form.supplierName}
              onChange={(e) => handleChange("supplierName", e.target.value)}
              placeholder="مثال: شركة النور للتوريدات"
            />
            {errors.supplierName && <span className={styles.error}>{errors.supplierName}</span>}
          </div>

          <div className={styles.field}>
            <label>اسم الصنف</label>
            <input
              type="text"
              value={form.itemName}
              onChange={(e) => handleChange("itemName", e.target.value)}
              placeholder="مثال: زيت"
            />
            {errors.itemName && <span className={styles.error}>{errors.itemName}</span>}
          </div>

          <div className={styles.row2}>
            <div className={styles.field}>
              <label>الكمية</label>
              <input
                type="number"
                min="0"
                value={form.quantity}
                onChange={(e) => handleChange("quantity", e.target.value)}
              />
              {errors.quantity && <span className={styles.error}>{errors.quantity}</span>}
            </div>

            <div className={styles.field}>
              <label>سعر الوحدة</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.unitPrice}
                onChange={(e) => handleChange("unitPrice", e.target.value)}
              />
              {errors.unitPrice && <span className={styles.error}>{errors.unitPrice}</span>}
            </div>
          </div>

          <div className={styles.summaryBox}>
            <div className={styles.summaryRow}>
              <span>الإجمالي</span>
              <strong>{totalAmount.toLocaleString("ar-EG")} ج.م</strong>
            </div>
            <div className={styles.summaryRow}>
              <span>المتبقي بعد الدفع</span>
              <strong className={remainingAmount > 0 ? styles.remainingNeg : styles.remainingOk}>
                {remainingAmount.toLocaleString("ar-EG")} ج.م
              </strong>
            </div>
          </div>

          <div className={styles.field}>
            <label>المبلغ المدفوع</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.paidAmount}
              onChange={(e) => handleChange("paidAmount", e.target.value)}
              placeholder="0.00"
            />
            {errors.paidAmount && <span className={styles.error}>{errors.paidAmount}</span>}
          </div>

          <div className={styles.field}>
            <label>تاريخ المعاملة</label>
            <input
              type="date"
              value={form.transactionDate}
              onChange={(e) => handleChange("transactionDate", e.target.value)}
            />
            {errors.transactionDate && <span className={styles.error}>{errors.transactionDate}</span>}
          </div>

          <div className={styles.field}>
            <label>ملاحظات</label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder="ملاحظات إضافية (اختياري)"
            />
          </div>

          <div className={styles.actions}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>إلغاء</button>
            <button type="submit" className={styles.submitBtn} disabled={submitting}>
              {submitting ? "جارِ الحفظ..." : "حفظ"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupplierFormModal;